import { useState } from "react";
import { Button } from "@/ui/button";
import { cn } from "@/lib/utils";
import { useAcoes, useRecado } from "../estado.jsx";

/**
 * O aviso de rodapé: erro do servidor em português, ou o que acabou de sair.
 *
 * É o ÚNICO canal de retorno da mesa — todo `avisar()` cai aqui, inclusive
 * "Gravado no CRM, mas NÃO enviado" e toda recusa de permissão. Por isso:
 *
 *   - as regiões ao vivo ficam SEMPRE montadas e só o texto por dentro troca.
 *     Região ao vivo que nasce junto com o recado não é anunciada em parte dos
 *     navegadores, e um envio que não saiu não pode acontecer em silêncio;
 *   - DUAS regiões, e o recado cai numa só: `alert` (assertivo) para falha,
 *     `status` (educado) para o que deu certo — "Enviado" interrompendo o leitor
 *     de tela no meio de uma frase é ruído. O recado é texto (tratado como
 *     falha, que é o que quase todo `avisar()` é hoje) ou `{ texto, tom }` com
 *     `tom: "ok"` para o que deu certo;
 *   - ENTRA de baixo, por transição (a gaveta, .32s) e SAI mais rápido (.16s):
 *     a saída é o fim de uma coisa já lida, e não pede atenção;
 *   - com o ponteiro EM CIMA ele não some. O relógio de 6s é do `estado.jsx`; se
 *     ele acabar enquanto a pessoa lê, o texto fica até o ponteiro sair — recado
 *     que foge de quem está lendo é recado que não foi lido.
 *
 * O `Toast` do shadcn NÃO serve aqui: ele monta o recado num portal quando o
 * recado chega (a região ao vivo nasceria junto com a mensagem, que é
 * justamente o que não pode) e depende de um `ToastProvider` na casca — a fila é
 * do `estado.jsx`, um recado por vez, e não da primitiva.
 *
 * O que fecha é um `<button>`: o `<div onClick>` de antes não tinha como ser
 * dispensado pelo teclado.
 */
const lerRecado = (recado) => (recado == null ? null : typeof recado === "string" ? { texto: recado, tom: "erro" } : recado);

export default function Recado() {
  const bruto = useRecado();
  const { fecharRecado } = useAcoes();
  /* O último recado fica guardado para a SAÍDA: sem ele o texto sumiria no
     primeiro quadro e não haveria o que deslizar para fora. Ajuste durante o
     render (o padrão do React para "lembrar o valor anterior"), e não efeito:
     com efeito, o recado novo pintava um quadro com o texto velho. */
  const [guardado, setGuardado] = useState(bruto);
  if (bruto != null && bruto !== guardado) setGuardado(bruto);
  const [sob, setSob] = useState(false);
  const recado = lerRecado(bruto);
  const ultimo = lerRecado(guardado);

  const dentro = Boolean(recado) || (sob && Boolean(ultimo));
  const visto = recado ?? ultimo;
  const ok = visto?.tom === "ok";

  const caixa = visto ? (
    <Button
      variant="default"
      onClick={() => {
        setSob(false);
        fecharRecado();
      }}
      onPointerEnter={() => setSob(true)}
      onPointerLeave={() => setSob(false)}
      data-dica="Fechar o aviso"
      tabIndex={dentro ? undefined : -1}
      /* Recado longo tem de quebrar linha: o botão do preset nasce
         `whitespace-nowrap`, e a recusa de permissão é uma frase inteira. O fio
         de 3px à esquerda diz se foi falha (terra clara) ou acerto (verde vivo)
         sem depender só da frase. */
      className={cn(
        "h-auto min-h-toque justify-start rounded-cartao border-0 border-l-[3px] bg-escuro px-4 py-[11px] text-left text-corpo font-normal whitespace-normal text-sobre-escuro shadow-[0_18px_40px_-10px_color-mix(in_oklab,_var(--color-escuro)_40%,_transparent)] hover:bg-escuro",
        ok ? "border-l-verde-vivo" : "border-l-terra-clara",
        "transition-[translate,opacity,visibility]",
        dentro
          ? /* `starting:` é o quadro de antes do primeiro: sem ele o recado que
               monta a caixa pela primeira vez nascia já no lugar, sem entrar. */
            "pointer-events-auto visible translate-y-0 opacity-100 duration-[320ms] ease-gaveta starting:translate-y-[calc(100%+24px)] starting:opacity-0"
          : "pointer-events-none invisible translate-y-[calc(100%+24px)] opacity-0 duration-[var(--t-curta)] ease-entra",
      )}
    >
      {visto.texto}
    </Button>
  ) : null;

  return (
    /* Acima do rodapé, sempre: `--rodape` é a medida de agora (58px de barra de
       abas mais a faixa de gestos no telefone, 12 no computador, zero dentro de
       uma conversa). Fixo em 22px, o recado nascia POR BAIXO da barra de abas
       no celular — e recado que não se lê é recado que não existe. */
    <div className="pointer-events-none absolute bottom-[calc(22px+var(--rodape,0px))] left-1/2 z-[60] max-w-[min(520px,92%)] -translate-x-1/2">
      <div role="alert">{ok ? null : caixa}</div>
      <div role="status">{ok ? caixa : null}</div>
    </div>
  );
}
